import { watchedStatusQuery } from '@/queries/watchlistQuery'

type UseWatchedStatusWhileOpenParams = {
  personId: string
  listingId: string
  /** The read runs only while the note editor is open. */
  isOpen: boolean
  /** False where the editor never needs the watched state, e.g. a closed card. */
  needsWatchedState: boolean
  /** The watched state when the caller already knows it; then nothing is read. */
  knownWatchlisted?: boolean
}

type WatchedStatusWhileOpen = {
  /** Undefined until it is known, so a new note never watches on a guess. */
  wasWatchlisted: boolean | undefined
  /**
   * True when the watched state cannot be known for now: the read failed, is
   * retrying, or cannot run (no person). NotesWidget then saves a new note
   * without watching instead of waiting.
   */
  isWatchedStateError: boolean
}

/**
 * The watched state a note editor in a popover needs, read only while the
 * popover is open. Only the first attempt of the read reports "unknown"
 * (`wasWatchlisted` undefined, no error), which keeps the editor waiting.
 */
export const useWatchedStatusWhileOpen = ({
  personId,
  listingId,
  isOpen,
  needsWatchedState,
  knownWatchlisted,
}: UseWatchedStatusWhileOpenParams): WatchedStatusWhileOpen => {
  const { data, fetchStatus, failureCount } = watchedStatusQuery.useWithOpts(
    [{ personId, listingId }],
    {
      enabled:
        isOpen &&
        needsWatchedState &&
        knownWatchlisted === undefined &&
        !!personId,
    }
  )
  if (knownWatchlisted !== undefined) {
    return { wasWatchlisted: knownWatchlisted, isWatchedStateError: false }
  }
  const isFirstAttemptInFlight =
    fetchStatus === 'fetching' && failureCount === 0
  return {
    wasWatchlisted: data?.isWatchlisted,
    isWatchedStateError: data === undefined && !isFirstAttemptInFlight,
  }
}
